import axios from 'axios';
import { getTokens } from './auth';

export interface GuardianPermission {
  /** Resource name, e.g. 'Approvals' — the tail of a Guardian permission string. */
  name: string;
  /** CRUD actions held on that resource, e.g. ['Read', 'Create']. */
  scope: string[];
}

export interface GuardianOrganization {
  name: string;
  id: string;
  relationId: number | null;
}

export interface GuardianProfile {
  tenantId: string;
  /**
   * The authorization decision, made by the backend. The browser deliberately
   * does not re-derive it: the server enforces the same rule on every business
   * route, and duplicating the predicate here would let the two drift apart.
   */
  isAuthorized: boolean;
  /**
   * Role names. In Guardian v3 the role is the middle segment of each
   * permission string (`<APP-KEY>:<Role Name>:<Resource>.<Action>`); the
   * backend extracts it.
   */
  roles: string[];
  permissions: GuardianPermission[];
  organizations: GuardianOrganization[];
  /** What the backend was looking for — shown on denial to aid diagnosis. */
  expected: {
    allowedRoles: string[];
    requiredPermission: string;
  };
  /** What a denied user should actually go and request. */
  accessRequestHint?: string;
  /** Unparsed permission strings, for support and debugging. */
  rawPermissions: string[];
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const AUTH_BYPASS = String(process.env.REACT_APP_AUTH_BYPASS || '').toLowerCase() === 'true';

function buildBypassProfile(): GuardianProfile {
  return {
    tenantId: 'LOCAL_DEV',
    isAuthorized: true,
    roles: ['AI-Assist'],
    permissions: [{ name: 'LocalDev', scope: ['Access'] }],
    organizations: [{ name: 'Local Development', id: 'local-dev', relationId: null }],
    expected: { allowedRoles: ['AI-Assist'], requiredPermission: 'LocalDev.Access' },
    accessRequestHint: 'Local development auth bypass is enabled.',
    rawPermissions: ['LOCAL_DEV:AI-Assist:LocalDev.Access'],
  };
}

function toGuardianError(reason: unknown): Error {
  const responseStatus = (reason as { response?: { status?: unknown } })?.response?.status;
  const responseMessage = (reason as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  if (responseStatus === 502 || responseStatus === 503 || responseStatus === 504) {
    return new Error('Authentication service is temporarily unavailable');
  }
  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return new Error(responseMessage);
  }

  if (reason instanceof Error) {
    if (responseStatus === 401) {
      return new Error('Guardian profile request was rejected (401 Unauthorized)');
    }
    return reason.message === 'Network Error'
      ? new Error('Authentication service is temporarily unavailable')
      : reason;
  }

  if (typeof reason === 'string') {
    return new Error(reason);
  }

  return new Error('Failed to load Guardian profile');
}

/**
 * Fetches the caller's Guardian roles and authorization decision.
 *
 * No identifier is sent: the backend derives `linkUuid` from the `sub` claim of
 * the verified Sentinel token, so a caller cannot request another user's
 * entitlements.
 */
export async function fetchGuardianProfile(currentAccessToken?: string): Promise<GuardianProfile> {
  if (AUTH_BYPASS) {
    return buildBypassProfile();
  }

  // Prefer the token held by react-oidc-context. It is the token that made the
  // current authenticated render possible and may have been silently renewed
  // after the last sessionStorage write. Session storage remains a fallback
  // for callers outside the provider and for local test tooling.
  const { accessToken: storedAccessToken } = getTokens();
  const accessToken = currentAccessToken || storedAccessToken;
  if (!accessToken) {
    throw new Error('No Sentinel access token available');
  }

  try {
    const response = await axios.get<GuardianProfile>(
      `${API_BASE_URL}/guardianUserProfile`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );

    return response.data;
  } catch (error) {
    throw toGuardianError(error);
  }
}

/** Checks whether a permission is held, optionally at a specific scope. */
export function hasPermission(
  permissions: GuardianPermission[],
  required: { name: string; scope?: string },
): boolean {
  return permissions.some((perm) => (
    perm.name === required.name
    && (required.scope === undefined
      ? perm.scope.length > 0
      : perm.scope.includes(required.scope))
  ));
}
